import {
  Badge,
  Button,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { IconSend, IconShoppingCart } from '@tabler/icons-react';
import {
  bindHover,
  bindPopover,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import HoverPopover from 'material-ui-popup-state/HoverPopover';
import { Lite } from 'types/api';
import {
  PurchaseRequestGroupSchema,
  PurchaseRequestGroupType,
} from 'types/clientV2/purchaseRequests';
import { useActivePurchaseRequestGroups } from 'ui-component/AddToCartPopover/hooks/useActivePurchaseRequestGroups';
import {
  useGetPurchaseRequestGroupDetail,
  PurchaseRequestCartSchema,
} from 'views/simplifiedPurchasing/hooks/useGetPurchaseRequestGroupDetail';
import { makeBuyableColumns } from 'views/simplifiedPurchasing/Cart/columns';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ALL_VIEWS } from 'constants/appConstants';
import { getTypeConfig } from 'ui-component/AddToCartPopover/components/PurchaseRequestGroupSelection';
import { useSelector } from 'store';
import { drawerWidth } from 'constants/themeConstants';
import { findV2OrgPartById } from 'utils/v2OrgParts';
import { PartId } from 'types/part';
import useRequestDecoratedPartData from 'hooks/useRequestDecoratedPartData';
import { PartSchemas } from 'types/partStatus';
import _ from 'lodash';
import { calcBestUnitPrice } from 'utils/offerCalculations';
import { OrgPartSchema } from 'types/clientV2/parts';
import { makeRfqPartsColumns } from 'views/simplifiedPurchasing/RFQCart/components/RFQPartsDatagrid/columns';

const MAX_GROUPS_TO_SHOW = 5;
export default function ActiveCartsSummaryWidget() {
  const [showAll, setShowAll] = useState(false);
  const { activeGroups: activeCartGroups } = useActivePurchaseRequestGroups(
    PurchaseRequestGroupType.CART
  );
  const { activeGroups: activeRfqGroups } = useActivePurchaseRequestGroups(
    PurchaseRequestGroupType.RFQ
  );
  const { drawerOpen } = useSelector((state) => state.menu);
  if (!drawerOpen) {
    return null;
  }
  const topGroups = showAll
    ? [...activeCartGroups, ...activeRfqGroups]
    : [...activeCartGroups, ...activeRfqGroups].slice(0, MAX_GROUPS_TO_SHOW);
  return (
    <Stack
      direction="column"
      spacing={1.5}
      sx={{
        pl: 1,
        pb: 1,
        pr: 2,
        width: '100%',
        maxWidth: `${drawerWidth - 8}px`,
        flexWrap: 'nowrap',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      {topGroups.map((group) => (
        <PurchaseRequestGroupRow key={group.id} group={group} />
      ))}
      {activeCartGroups.length + activeRfqGroups.length >
        MAX_GROUPS_TO_SHOW && (
        <Divider>
          <Button
            variant="text"
            color="primary"
            size="small"
            onClick={() => {
              setShowAll(!showAll);
            }}
          >
            {showAll
              ? 'Collapse'
              : `Show ${
                  activeCartGroups.length +
                  activeRfqGroups.length -
                  MAX_GROUPS_TO_SHOW
                } More`}
          </Button>
        </Divider>
      )}
    </Stack>
  );
}

function PurchaseRequestGroupRow({
  group,
}: {
  group: Pick<
    Lite<PurchaseRequestGroupSchema, never>,
    'id' | 'name' | 'createdAt' | 'type' | 'numLines'
  >;
}) {
  const { cartId, purchaseRequestGroupId } = useParams<
    'cartId' | 'purchaseRequestGroupId'
  >();
  const popupState = usePopupState({
    variant: 'popover',
    popupId: `mouse-over-popover-${group.id}`,
  });
  const { purchaseRequestGroup, purchaseRequests, isLoading } =
    useGetPurchaseRequestGroupDetail(group.id);
  const typeConfig = getTypeConfig(group.type);
  const Icon = typeConfig.icon;
  const navigate = useNavigate();
  const handleEditCart = () => {
    const isRfq = group.type === PurchaseRequestGroupType.RFQ;
    const successLink = isRfq
      ? `${ALL_VIEWS.OPEN_RFQ.path}/${group.id}`
      : `${ALL_VIEWS.OPEN_CART.path}/${group.id}`;
    navigate(successLink);
  };

  return (
    <Badge
      badgeContent={purchaseRequests?.length || group.numLines}
      color={
        purchaseRequestGroupId === group.id || cartId === group.id
          ? 'success'
          : 'primary'
      }
      max={99}
    >
      <Button
        {...bindHover(popupState)}
        variant="outlined"
        startIcon={<Icon />}
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'center',
          overflowX: 'hidden',
        }}
        fullWidth
        onClick={handleEditCart}
        color={
          purchaseRequestGroupId === group.id || cartId === group.id
            ? 'success'
            : 'primary'
        }
      >
        <Typography noWrap>{group.name}</Typography>
      </Button>
      {popupState.isOpen && (
        <HoverPopover
          {...bindPopover(popupState)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {isLoading && (
            <Skeleton variant="rectangular" height={100} width={300} />
          )}
          {!isLoading && !purchaseRequestGroup && (
            <Typography>No data found</Typography>
          )}
          {!isLoading && purchaseRequestGroup && (
            <PurchaseRequestSummary
              purchaseRequests={purchaseRequests}
              purchaseRequestGroup={purchaseRequestGroup}
              onClose={popupState.close}
              isLoading={isLoading}
            />
          )}
        </HoverPopover>
      )}
    </Badge>
  );
}

function PurchaseRequestSummary({
  purchaseRequests,
  purchaseRequestGroup,
  onClose,
  isLoading,
}: {
  purchaseRequests: PurchaseRequestCartSchema[];
  purchaseRequestGroup: Lite<
    PurchaseRequestGroupSchema,
    'org' | 'requestedBy' | 'destinationLocation'
  >;
  onClose: () => void;
  isLoading: boolean;
}) {
  const theme = useTheme();
  const isRfq = purchaseRequestGroup.type === PurchaseRequestGroupType.RFQ;
  const cpids = useMemo(() => {
    if (!purchaseRequests.length) {
      return [];
    }
    return _.uniq(
      _.chain(purchaseRequests)
        .flatMap((pr) => {
          const prCpids: PartId[] = [];
          const v2OrgPart = findV2OrgPartById(pr.v2OrgParts, pr.v2OrgPart);
          if (v2OrgPart?.cofactrId) {
            prCpids.push(v2OrgPart.cofactrId);
          }
          _.chain(pr.altV2OrgParts || [])
            .map((altV2OrgPartId) => {
              const altV2OrgPart = findV2OrgPartById(
                pr.v2OrgParts,
                altV2OrgPartId
              );
              return altV2OrgPart?.cofactrId;
            })
            .compact()
            .forEach((cpid) => prCpids.push(cpid))
            .value();
          return prCpids;
        })
        .flatten()
        .compact()
        .value()
    );
  }, [purchaseRequests]);

  const { data: partData } = useRequestDecoratedPartData({
    partIds: cpids,
    schemas: [PartSchemas.core, PartSchemas.supply],
    cacheKey: `cartOptimizePartData`,
  });

  const rows = useMemo(
    () =>
      purchaseRequests.slice(0, 3).map((pr) => {
        const supplies = _.chain(pr.v2OrgParts)
          .map(
            (v2OrgPart) =>
              partData[v2OrgPart.cofactrId || '']?.[PartSchemas.supply]
          )
          .compact()
          .value();
        const supplierObjectId = pr.orgSupplier?.objectId;
        const firstSupply = _.first(supplies);
        const supply =
          supplierObjectId && firstSupply?.offers
            ? {
                ...firstSupply,
                offers: _.filter(
                  firstSupply.offers,
                  (offer) => offer.supplier.id === supplierObjectId
                ),
              }
            : firstSupply;
        const altsSupply = supplierObjectId
          ? _.slice(supplies, 1)
              .filter((altSply) => altSply?.offers && altSply.offers.length > 0)
              .map((altSply) => ({
                ...altSply,
                offers: _.filter(
                  altSply.offers,
                  (offer) => offer.supplier.id === supplierObjectId
                ),
              }))
          : _.slice(supplies, 1);

        const cheapestUnitPrice = calcBestUnitPrice({
          part: _.find(
            pr.v2OrgParts,
            (v2OrgPart) => v2OrgPart.id === pr.v2OrgPart
          ) as OrgPartSchema,
          supply,
          altsSupply,
          quant: pr.quant,
        });

        const hasSupply = supplies.length > 0;
        const hasOffers = _.some(
          supplies,
          (s) => s?.offers && s.offers.length > 0
        );
        const hasValidPrice = cheapestUnitPrice > 0;
        const isNonBuyable = !hasSupply || !hasOffers || !hasValidPrice;

        return {
          ...pr,
          optimizedQuant: pr.quant,
          optimizedUnitPrice: cheapestUnitPrice,
          optimizedLineTotal: cheapestUnitPrice * pr.quant,
          isNonBuyable,
        };
      }),
    [purchaseRequests, partData]
  );

  const navigate = useNavigate();
  const handleEditCart = () => {
    const successLink = isRfq
      ? `${ALL_VIEWS.OPEN_RFQ.path}/${purchaseRequestGroup.id}`
      : `${ALL_VIEWS.OPEN_CART.path}/${purchaseRequestGroup.id}`;
    onClose();
    navigate(successLink);
  };

  const paperHeight = useMemo(() => {
    const numRows = rows.length;
    const baseHeight = 350;
    const heightPerRow = 50;
    const maxRows = 3;
    return baseHeight - (maxRows - numRows) * heightPerRow;
  }, [rows.length]);

  return (
    <Paper sx={{ height: `${paperHeight}px`, width: '700px' }}>
      <Stack sx={{ height: '100%' }}>
        <Typography sx={{ p: 2 }} variant="h5">
          {purchaseRequestGroup.name}
        </Typography>
        <DataGridPremium
          rows={rows}
          columns={
            isRfq
              ? makeRfqPartsColumns({
                  tempUoM: true,
                  editable: false,
                  noRowHeightAuto: true,
                })
              : makeBuyableColumns({
                  tempUoM: true,
                  editable: false,
                  noRowHeightAuto: true,
                })
          }
          loading={isLoading}
          getRowHeight={() => 'auto'}
          getRowClassName={(params) =>
            params.row.isNonBuyable ? 'warning-row' : ''
          }
          hideFooter
          disableColumnReorder
          disableSelectionOnClick
          sx={{
            '& ::-webkit-scrollbar': {
              width: '6px',
              height: '6px',
            },
            '& ::-webkit-scrollbar-track': {
              backgroundColor: '#f5f5f5',
            },
            '& ::-webkit-scrollbar-thumb': {
              borderRadius: '10px',
              backgroundColor: '#c4c4c4',
            },
            '.MuiDataGrid-cell--withRenderer': {
              overflow: 'hidden',
              width: '100%',
            },
            '& .MuiDataGrid-cell': {
              px: '3px',
            },
            '& .MuiDataGrid-row.warning-row': {
              backgroundColor: theme.palette.warning.light,
              border: `1px solid ${theme.palette.warning.main}`,
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: theme.palette.warning.light,
              },
              '&.Mui-hovered': {
                backgroundColor: theme.palette.warning.light,
              },
            },
            '& .MuiDataGrid-row:not(.warning-row)': {
              '&.Mui-hovered': {
                background: 'white',
              },
              '&:hover': {
                background: 'white',
              },
            },
          }}
        />
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ p: 2 }}
          alignItems="center"
        >
          <Typography>
            Showing {rows.length}/{purchaseRequests.length} lines
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={
              purchaseRequestGroup.type === PurchaseRequestGroupType.CART ? (
                <IconShoppingCart />
              ) : (
                <IconSend />
              )
            }
            onClick={handleEditCart}
          >
            {purchaseRequestGroup.type === PurchaseRequestGroupType.CART
              ? 'Edit Cart or Checkout'
              : 'Edit or Submit RFQ'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
